type Ptr = { id: number; x: number; y: number; sx: number; sy: number; t0: number };

/**
 * Keyboard + pointer. Modes read state each frame; call endFrame() last.
 * Mobile: one-finger drag = orbit, pinch = zoom, short tap = place.
 */
export class Input {
  readonly keys = new Set<string>();
  /** NDC pointer: x,y in [-1,1], y up */
  pointer = { x: 0, y: 0, down: false, justDown: false, justUp: false };
  /** True for one frame after a short canvas tap (not a drag). */
  tapPlace = false;
  /** Per-frame orbit delta in CSS pixels (positive dx = finger right). */
  orbit = { dx: 0, dy: 0 };
  /** Per-frame pinch delta (positive = fingers apart). */
  pinch = 0;

  private justPressedKeys = new Set<string>();
  private justReleasedKeys = new Set<string>();
  private canvas: HTMLCanvasElement;
  private pointers = new Map<number, Ptr>();
  private lastPinchDist = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    canvas.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("pointercancel", this.onPointerUp);
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  dispose() {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("pointercancel", this.onPointerUp);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
  }

  endFrame() {
    this.pointer.justDown = false;
    this.pointer.justUp = false;
    this.tapPlace = false;
    this.orbit.dx = 0;
    this.orbit.dy = 0;
    this.pinch = 0;
    this.justPressedKeys.clear();
    this.justReleasedKeys.clear();
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  justPressed(code: string): boolean {
    return this.justPressedKeys.has(code);
  }

  justReleased(code: string): boolean {
    return this.justReleasedKeys.has(code);
  }

  /** UI buttons inject a one-frame key press. */
  pulse(code: string) {
    this.justPressedKeys.add(code);
    this.keys.add(code);
    queueMicrotask(() => this.keys.delete(code));
  }

  private onKeyDown = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"].includes(e.code)) {
      e.preventDefault();
    }
    if (e.repeat) return;
    this.keys.add(e.code);
    this.justPressedKeys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return;
    this.keys.delete(e.code);
    this.justReleasedKeys.add(e.code);
  };

  private onPointerDown = (e: PointerEvent) => {
    this.canvas.setPointerCapture(e.pointerId);
    this.updatePointer(e);
    this.pointer.down = true;
    this.pointer.justDown = true;
    this.pointers.set(e.pointerId, {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      sx: e.clientX,
      sy: e.clientY,
      t0: performance.now(),
    });
    if (this.pointers.size === 2) this.lastPinchDist = this.pinchDist();
  };

  private onPointerUp = (e: PointerEvent) => {
    this.updatePointer(e);
    const p = this.pointers.get(e.pointerId);
    this.pointers.delete(e.pointerId);
    if (this.pointers.size === 0) {
      this.pointer.down = false;
      this.pointer.justUp = true;
    }
    if (!p || this.pointers.size > 0) return;
    const dist = Math.hypot(e.clientX - p.sx, e.clientY - p.sy);
    const dt = performance.now() - p.t0;
    if (dist < 16 && dt < 400) this.tapPlace = true;
  };

  private onPointerMove = (e: PointerEvent) => {
    this.updatePointer(e);
    const p = this.pointers.get(e.pointerId);
    if (!p) return;
    const dx = e.clientX - p.x;
    const dy = e.clientY - p.y;
    p.x = e.clientX;
    p.y = e.clientY;

    if (this.pointers.size === 1) {
      this.orbit.dx += dx;
      this.orbit.dy += dy;
    } else if (this.pointers.size >= 2) {
      const d = this.pinchDist();
      if (this.lastPinchDist > 0) this.pinch += d - this.lastPinchDist;
      this.lastPinchDist = d;
    }
  };

  private pinchDist(): number {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0]!.x - pts[1]!.x, pts[0]!.y - pts[1]!.y);
  }

  private updatePointer(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    this.pointer.x = x;
    this.pointer.y = y;
  }
}
